/* ─── Notification Service — Firestore listeners + mark read ──────── */

import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
  getDocs,
  Unsubscribe,
} from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import type { AppNotification } from '../types/social';

/** Real-time listener for unread notifications. Returns unsubscribe fn. */
export function subscribeToNotifications(
  uid: string,
  onData: (notifications: AppNotification[]) => void,
  onError?: (err: Error) => void,
  maxItems = 30
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'users', uid, 'notifications'),
    orderBy('createdDate', 'desc'),
    limit(maxItems)
  );
  return onSnapshot(
    q,
    (snap) => {
      const items: AppNotification[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as AppNotification[];
      onData(items);
    },
    (err) => onError?.(err)
  );
}

/** Mark a single notification as read */
export async function markNotificationRead(uid: string, notificationId: string): Promise<void> {
  await updateDoc(
    doc(FIRESTORE_DB, 'users', uid, 'notifications', notificationId),
    { readStatus: true }
  );
}

/**
 * Mark all unread notifications as read.
 *
 * We read the whole notifications collection and mark any doc that isn't already
 * read, rather than querying `where('readStatus','==',false)`. That equality
 * query silently SKIPS docs missing the `readStatus` field (e.g. legacy/other
 * creation paths that write a different field) — yet the UI counts those as
 * unread via `!n.readStatus`, so the old query left them behind and "mark all
 * read" appeared to do nothing (PPL-158). We deliberately DON'T `orderBy`/`limit`
 * either: an `orderBy('createdDate')` drops field-less docs, and a `limit` would
 * leave genuinely-unread docs older than the page uncleared. Filtering
 * client-side on `readStatus !== true` clears every kind. Batches cap at 500
 * writes, so we chunk. Mark-all is a rare, explicit action, and the notification
 * GC bounds the collection size, so the full read is acceptable.
 */
export async function markAllNotificationsRead(uid: string): Promise<void> {
  const snap = await getDocs(
    collection(FIRESTORE_DB, 'users', uid, 'notifications')
  );
  const unread = snap.docs.filter(
    (d) => (d.data() as { readStatus?: boolean }).readStatus !== true
  );
  if (unread.length === 0) return;
  for (let i = 0; i < unread.length; i += 500) {
    const batch = writeBatch(FIRESTORE_DB);
    unread.slice(i, i + 500).forEach((d) => batch.update(d.ref, { readStatus: true }));
    await batch.commit();
  }
}

/**
 * Get navigation target for a notification.
 *
 * PPL-531 — the switch that used to live here named eleven types and sent three
 * of them to `/tether`, which is not a route in `App.jsx`. The whole map is now
 * `services/notificationRoutes`, a firebase-free table the compiler checks for
 * completeness; this re-export keeps the two tray components' import unchanged.
 */
export { getNotificationLink } from './notificationRoutes';

/** Icon class per notification type */
export function getNotificationIcon(type: string): string {
  switch (type) {
    case 'personal':
    case 'background':
    // PPL-489 — the other two starter writers.
    case 'personal_message_triggered_push_ai':
    case 'conversation starters':
      return 'fa-user';
    case 'boosters':
      return 'fa-bolt';
    case 'message':
      return 'fa-envelope';
    case 'circle_message':
      return 'fa-comments';
    case 'circle_event':
      return 'fa-calendar';
    case 'tether_combined_notification':
    case 'tether_reply':
    case 'tether_interaction':
      return 'fa-gift';
    case 'relationship tips':
      return 'fa-lightbulb';
    // PPL-334 — the review nudge.
    case 'writeback_review':
      return 'fa-bolt';
    // PPL-460 — f8's reply to a channel message, same bolt as its other nudges.
    case 'channel_reply':
      return 'fa-bolt';
    case 'holiday':
      return 'fa-star';
    // PPL-531 — types the tray was already receiving and drawing a bell for.
    // PPL-277/340 — calendar life: the brief, the capture nudge, the radar.
    case 'premeet_brief':
    case 'postevent_capture':
    case 'calendar_birthday':
      return 'fa-calendar';
    case 'birthday':
      return 'fa-gift';
    // PPL-431 — a message, and the request to send you one.
    case 'chat_message':
    case 'message_request':
      return 'fa-envelope';
    case 'circle_event_reminder':
      return 'fa-calendar';
    case 'circle_invite':
    case 'circle_invite_accepted':
    case 'circle_invite_declined':
      return 'fa-user-plus';
    // Prompts to add people — the same person glyph the starters use.
    case 'add_people_prompt_ai':
    case 'intelligent_push_people_fallback':
      return 'fa-user';
    case 'mediation_invite':
    case 'mediation_accepted':
    case 'mediation_declined':
    case 'mediation_prep_ended':
    case 'mediation_paused':
    case 'mediation_rules_confirmed':
    case 'mediation_room_ready':
    case 'mediation_checkin':
    case 'mediation_room_open':
    case 'mediation_ended_summary':
    case 'mediation_resumed':
    case 'mediation_deleted':
      return 'fa-handshake';
    default:
      return 'fa-bell';
  }
}
